import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import { ArbeitsbereichService } from '../../../shared/service/arbeitsbereich.service';
import { Arbeitsbereich } from '../../../config/model/arbeitsbereich-config.model';
import { TranslateService } from '@ngx-translate/core';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import { MessageService } from '../../../core/service/message.service';
import { IdentifyService } from '../../../shared/service/identify.service';
import { ConfigService } from '../../../config/config.service';
import { WorkspaceNotifyService } from './workspace-notify.service';
import { takeUntil } from 'rxjs/operators';
import { TakeUntilDestroy } from '../../../core/component/take-until-destroy';

@Component({
    selector: 'arbeitsbereich',
    templateUrl: './arbeitsbereich.component.html',
    styleUrls: ['arbeitsbereich.component.scss'],
    standalone: false
})
export class ArbeitsbereichComponent extends TakeUntilDestroy implements OnInit, AfterViewInit, OnDestroy {
  public arbeitsbereiche: Arbeitsbereich[];
  public dropDownLabel: string;
  private _selectedArbeitsbereich: Arbeitsbereich;
  private _oldArbeitsBereich: Arbeitsbereich;

  constructor(
    public arbeitsbereichService: ArbeitsbereichService,
    private translateService: TranslateService,
    private layerContainerService: LayerContainerService,
    private messageService: MessageService,
    private identifyService: IdentifyService,
    private widgetNotifyService: WidgetNotifyService,
    private detectorRef: ChangeDetectorRef,
    private configService: ConfigService,
    private workspaceNotifyService: WorkspaceNotifyService
  ) {
    super();
    this.arbeitsbereiche = this.arbeitsbereichService.arbeitsBereiche;
  }

  ngAfterViewInit() {
    // set default selected arbeitsbereich active
    this.setDefaultWorkspace();

    this.workspaceNotifyService.selectWorkspace$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(ws => {
        if (ws) {
          this.selectBereich(ws);
          this.detectorRef.detectChanges();
          this.buildIdentifyTasks();
          this.workspaceNotifyService.onWorkspaceChanged(true);
          this.arbeitsbereichService.onChangeArbeitsbereichSubject.next(
            this.selectedArbeitsbereich
          );
        }
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngOnInit() {
    // set initial dropdown label for arbeitsbereich
    if (!this._selectedArbeitsbereich) {
      this.translateService.get('HEADER_PANEL.ARBEITSBEREICH.DROPDOWN_LABEL').subscribe(label => {
        this.dropDownLabel = label;
      });
    }

    // handle language change event and set the default option when arbeitsbereich not selected
    this.translateService.onLangChange.subscribe(() => {
      if (this.selectedArbeitsbereich) {
        // fetch new translation for changed language
        this.translateService.get(this.selectedArbeitsbereich.name).subscribe(label => {
          this.dropDownLabel = label;
        });
      }
    });
  }

  public get selectedArbeitsbereich(): Arbeitsbereich {
    return this._selectedArbeitsbereich;
  }

  public set selectedArbeitsbereich(ab: Arbeitsbereich) {
    this._selectedArbeitsbereich = ab;
  }

  /**
   * @function selectBereich
   * @description Set selected Arbeitsbereich. Hide layers of old
   * and show layers of new Arbeitsbereich
   * @param arbeitsbereich
   */
  public selectBereich(arbeitsbereich: Arbeitsbereich) {
    if (!arbeitsbereich || (arbeitsbereich.layers && arbeitsbereich.layers.length < 1)) {
      this.messageService.error('NOTIFICATIONS.ERROR.ARBEITSBEREICHE.CONIFIGARATION');
      return;
    }
    // Setze Visibility vom alten Arbeitsbereich auf false, falls vorhanden
    if (this._oldArbeitsBereich) {
      this.setVisibilityOnArbeitsbereichLayers(this._oldArbeitsBereich, false);
    }
    // Neuer Arbeitsberech als selected und als alt zuweisen
    this.arbeitsbereichService.setSelected(arbeitsbereich);
    this.selectedArbeitsbereich = arbeitsbereich;
    this._oldArbeitsBereich = arbeitsbereich;

    // fetch new translation for changed language
    this.translateService.get(this.selectedArbeitsbereich.name).subscribe(label => {
      this.dropDownLabel = label;
    });

    // Setze Visibility=true für alle Layers vom neuen Arbeitsbereich
    this.setVisibilityOnArbeitsbereichLayers(arbeitsbereich, true);
    // Notify List-View that arbeitsbereich has been changed
    this.widgetNotifyService.onArbeitsbereichChangedSubject.next(arbeitsbereich);
  }

  private setDefaultWorkspace(): void {
    if (!this.arbeitsbereichService.selectedArbeitsbereich) {
      this.arbeitsbereichService.setSelected(
        this.arbeitsbereiche.filter(f => f.isActive === true)[0]
      );
      this.selectBereich(this.arbeitsbereichService.selectedArbeitsbereich);
      this.detectorRef.detectChanges();
      this.buildIdentifyTasks();
    }
  }

  /**
   * @function setVisibilityOnArbeitsbereichLayers
   * @param arbeitsbereich
   * @param visible
   * @description Set all Layers of given Arbeitsbereich to hidden
   * if visible= false, else show if visible=true
   */
  private setVisibilityOnArbeitsbereichLayers(arbeitsbereich: Arbeitsbereich, visible: boolean) {
    if (arbeitsbereich.layers.length === 0) {
      this.messageService.warning('NOTIFICATIONS.ERROR.ARBEITSBEREICHE.CONIFIGARATION', 5000);
      return;
    }
    arbeitsbereich.layers.forEach(lyr => {
      const arbeitsbereichLyrs = this.layerContainerService.layerList.filter(
        f => f.esriLayer.id === lyr.fkLayer
      );
      if (!arbeitsbereichLyrs || arbeitsbereichLyrs.length < 1) {
        if (!visible) {
          return;
        }
        this.messageService.warning('NOTIFICATIONS.ERROR.ARBEITSBEREICHE.CONIFIGARATION', 5000);
        return;
      }
      arbeitsbereichLyrs.forEach(arbeitsbereichLyr => {
        arbeitsbereichLyr.esriLayer.visible = visible;
        if (arbeitsbereichLyr.ignoreLayerIds && arbeitsbereichLyr.ignoreLayerIds.length > 0) {
          arbeitsbereichLyr.esriLayer['allSublayers'].items.forEach(subLyrItem => {
            const ignoreLyr = arbeitsbereichLyr.ignoreLayerIds.filter(f => f === subLyrItem.id);
            ignoreLyr.length === 0
              ? (subLyrItem.visible = visible)
              : (subLyrItem.visible = !visible);
          });
        }
      });
    });
  }

  private buildIdentifyTasks(): void {
    const identifyConfig = this.configService.identifyConfig;
    if (!identifyConfig) {
      console.warn('No configration for identifier was found!');
      return;
    }
    identifyConfig.layers.forEach(lyr => {
      const identifyLyr = this.layerContainerService.layerList.filter(
        f => f.esriLayer.id === lyr.fkLayer
      );
      this.identifyService.buildIdentifyTask(identifyLyr);
    });
  }
}
